import { categoriesData } from "../../static/data";
import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import socketIOClient from 'socket.io-client';
import {
  AiOutlineHeart,
  AiOutlineShoppingCart,
  AiOutlineSearch,
  AiOutlineClose,
  AiOutlineStar
} from "react-icons/ai";
import {
 MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { BellIcon } from '@heroicons/react/24/outline';
import { IoIosArrowDown } from "react-icons/io";
import { CgProfile } from "react-icons/cg";
import Cart from "../cart/Cart";
import Wishlist from "../Wishlist/Wishlist";
import { RxCross1 } from "react-icons/rx";
import AvatarDropdown from "./AvatarDropdown";
import { useSelector, useDispatch } from "react-redux";
import {
  toggleCart,
  toggleWishlist,
  toggleAvatarDropdown,
  toggleTopLevelDropdown,
  toggleNotificationSidebar,
  setAvatarDropdownOpen,
  setTopLevelDropdownOpen,
} from "../../state/uiSlice";
import { loadNotifications, resetUnseenNotifications } from '../../redux/actions/notifications';
import TopLevelDropdown from "./TopLevelDropdown";
import SearchBar from "./SearchBar";
import ProfileSwitchButton from "../ProfileSwitchButton";
import MobileSearchBar from "./MobileSearchBar";
import SearchIcon from "./SearchIcon";
import NotificationSidebar from "../NotificationSidebar";
import { socketEndpoint } from "../../config/socket";
import matrixAvatar from "../../assets/church-login/matrix-avatar.png";
import { getProfileAvatarUrl } from "../../utils/avatarUrl";

const DEFAULT_AVATAR_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96"><rect width="96" height="96" rx="48" fill="#1f2937"/><circle cx="48" cy="36" r="18" fill="#d1d5db"/><path d="M18 82c5-20 17-30 30-30s25 10 30 30" fill="#d1d5db"/></svg>';
const DEFAULT_AVATAR_URL = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(DEFAULT_AVATAR_SVG)}`;
const notificationSocket = socketIOClient(socketEndpoint, { withCredentials: true });

const Header = () => {
  const { isAuthenticated, user, loading } = useSelector((state) => state.user);
  const { wishlist } = useSelector((state) => state.wishlist);
  const profileType = sessionStorage.getItem('currentProfile') || 'Guest'; 
  const cartState = useSelector(state => state.cart.carts); 
  const cart = cartState[profileType] || [];
  const [active, setActive] = useState(false);
  const [dropDown, setDropDown] = useState(false);
  const dropdownRef = useRef(null);
  const [open, setOpen] = useState(false);
  const isCartOpen = useSelector((state) => state.ui.isCartOpen);
  const isWishlistOpen = useSelector((state) => state.ui.isWishlistOpen);
  const isAvatarDropdownOpen = useSelector((state) => state.ui.isAvatarDropdownOpen);
  const isTopLevelDropdownOpen = useSelector((state) => state.ui.isTopLevelDropdownOpen);
  const currentProfile = useSelector((state) => state.user.currentProfile);
  const seller = useSelector((state) => state.seller.seller || state.seller.activeSeller);
  const { unseenCount } = useSelector((state) => state.notifications);
  const location = useLocation();
  
  const dispatch = useDispatch();
  const userId = user?._id;
  const sellerId = seller?._id;
  const notificationProfileId = currentProfile === 'User' ? userId : sellerId;
  const wishlistCount = Array.isArray(wishlist) ? wishlist.length : 0;
  const notificationCount = Number(unseenCount) || 0;
  const signedInAvatarUrl = getProfileAvatarUrl(user, DEFAULT_AVATAR_URL);

  const [isMobile, setIsMobile] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    handleResize(); 
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    dispatch(setAvatarDropdownOpen(false));
    dispatch(setTopLevelDropdownOpen(false));
    setOpen(false);
    setIsSearchModalOpen(false);
  }, [dispatch, location.pathname]);

  useEffect(() => {
    if (notificationProfileId) {
      dispatch(loadNotifications(currentProfile, notificationProfileId));
    }
  }, [dispatch, currentProfile, notificationProfileId]);

  useEffect(() => {
    if (!isTopLevelDropdownOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        dispatch(setTopLevelDropdownOpen(false));
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [dispatch, isTopLevelDropdownOpen]);

  useEffect(() => {
    if (!notificationProfileId) {
      return undefined;
    }

    const handleNotification = (notification = {}) => {
      const recipient = typeof notification.recipient === "object"
        ? notification.recipient?._id
        : notification.recipient;

      if (
        String(recipient || "") === String(notificationProfileId)
        && notification.recipientProfileType === currentProfile
      ) {
        dispatch(loadNotifications(currentProfile, notificationProfileId));
      }
    };

    notificationSocket.on('notification', handleNotification);

    return () => {
      notificationSocket.off('notification', handleNotification);
    };
  }, [dispatch, currentProfile, notificationProfileId]);

  const handleOpenWishlist = () => {
    dispatch(toggleWishlist());
  };

  const handleOpenCart = () => {
    dispatch(toggleCart()); 
  };

  const handleOpenNotifications = () => {
    dispatch(toggleNotificationSidebar());
    if (notificationProfileId) {
      dispatch(resetUnseenNotifications(notificationProfileId, currentProfile));
    }
  };

  const handleToggleDropdown = () => {
    dispatch(toggleTopLevelDropdown());
  };

  const handleToggleAvatarDropdown = () => {
    dispatch(toggleAvatarDropdown());
  };

  window.addEventListener("scroll", () => {
    if (window.scrollY > 70) {
      setActive(true);
    } else {
      setActive(false);
    }
  });

  const totalItems = cart.reduce((acc, item) => acc + item.qty, 0);

  const brandLockup = (
    <>
      <img
        src={matrixAvatar}
        className="main-header-component-brand-avatar"
        alt="Matrix avatar"
      />
      <span className="main-header-component-brand-text">
        <span className="main-header-component-brand-title" aria-label="MATRIX">
          {"MATRIX".split("").map((letter, index) => (
            <span key={`${letter}-${index}`} aria-hidden="true">{letter}</span>
          ))}
        </span>
        <span className="main-header-component-brand-subtitle">Gateway Community</span>
      </span>
    </>
  );

  return (
    <>
      <div className="main-header-component-container">
        <div className="main-header-component-wrapper">
        <div>
            <Link to="/" className="main-header-component-brand-link" aria-label="Matrix Gateway Community home">
              <span className="main-header-component-brand-lockup main-header-component-site-logo">
                {brandLockup}
              </span>
            </Link>
          </div>
          
          <div onClick={handleToggleDropdown} ref={dropdownRef}>
            <div className="main-header-component-categories">
              <img 
                src="https://res.cloudinary.com/dzlopmfj8/image/upload/v1724854900/memes/evajdtncfvfdnrihmx6t.png" 
                alt="Menu Icon" 
                className="header-icon" 
              />
              <button className="main-header-component-categories-button">
                Explore
              </button>
              <IoIosArrowDown
                size={20}
                className="main-header-component-arrow-icon"
                onClick={() => setDropDown(!dropDown)}
              />
              {isTopLevelDropdownOpen && (
                <TopLevelDropdown
                  categoriesData={categoriesData}
                  setDropDown={setDropDown}
                />
              )}
            </div>
          </div>

          {/* Desktop SearchBar */}
          {!isMobile && <SearchBar />}

          <div className="main-header-component-actions">
            <div className="main-header-component-action">
              <div
                className="main-header-component-wishlist"
                onClick={handleOpenWishlist}
              >
                <AiOutlineStar size={30} className="main-header-component-wishlist-icon" />
                {wishlistCount > 0 && (
                  <span className="main-header-component-wishlist-count">
                    {wishlistCount}
                  </span>
                )}
              </div>
            </div>

            <div className="main-header-component-action">
              <div
                className="main-header-component-cart"
                onClick={handleOpenCart}
              >
                <AiOutlineShoppingCart size={30} className="main-header-component-wishlist-icon" />
                {totalItems > 0 && (
                  <span className="main-header-component-cart-count">
                    {totalItems}
                  </span>
                )}
              </div>
            </div>

            <div className="main-header-component-action">
              <button
                type="button"
                className="main-header-component-notifications"
                onClick={handleOpenNotifications}
                aria-label="Open notifications"
              >
                <BellIcon className="main-header-component-notification-icon" aria-hidden="true" />
                {notificationCount > 0 && (
                  <span className="main-header-component-notification-count">
                    {notificationCount}
                  </span>
                )}
              </button>
            </div>

            <div className="main-header-component-action">
              <div className="main-header-component-avatar" onClick={handleToggleAvatarDropdown}>
                {loading !== false && !user ? (
                  <CgProfile size={30} color="rgb(255 255 255 / 83%)" />
                ) : isAuthenticated && user ? (
                  <>
                    <img
                      src={signedInAvatarUrl}
                      alt={`${currentProfile} Avatar`}
                      className="main-header-component-avatar-img"
                      onError={(event) => {
                        if (event.currentTarget.src !== DEFAULT_AVATAR_URL) {
                          event.currentTarget.src = DEFAULT_AVATAR_URL;
                        }
                      }}
                    />
                  </>
                ) : (
                  <Link to="/login">
                    <CgProfile size={30} color="rgb(255 255 255 / 83%)" />
                  </Link>
                )}
              </div>
              {isAuthenticated && isAvatarDropdownOpen && <AvatarDropdown />}
            </div>

            {isCartOpen && <Cart />}
            {isWishlistOpen && <Wishlist />}
          </div>
        </div>
      </div>

      <div
        className={`main-header-component-mobile-header ${active ? "active" : ""}`}
      >
        <div className="main-header-component-mobile-header-inner">
          <div className="main-header-component-mobile-discover">
            <div className="mobile-header-icon-container">
              <img
                src="https://res.cloudinary.com/dzlopmfj8/image/upload/v1724854900/memes/evajdtncfvfdnrihmx6t.png"
                alt="Discover Icon"
                className="mobile-header-icon"
                onClick={() => setOpen(true)}
              />
            </div>

            {/* Mobile Search Icon placed here */}
            {isMobile && (
              <div className="mobile-header-search-icon" onClick={() => setIsSearchModalOpen(true)}>
                <SearchIcon />
              </div>
            )}
          </div>

          <div className="main-header-component-mobile-logo-container">
            <Link to="/" className="main-header-component-brand-link" aria-label="Matrix home">
              <span className="main-header-component-brand-lockup main-header-component-mobile-logo">
                {brandLockup}
              </span>
            </Link>
          </div>
          
          <div className="main-header-component-mobile-actions">
            <div
              className="main-header-component-mobile-cart"
              onClick={handleOpenCart}
            >
              <AiOutlineShoppingCart size={30} />
              {totalItems > 0 && (
                <span className="main-header-component-mobile-cart-count">
                  {totalItems}
                </span>
              )}
            </div>
            <button
              type="button"
              className="main-header-component-mobile-notifications"
              onClick={handleOpenNotifications}
              aria-label="Open notifications"
            >
              <BellIcon className="main-header-component-mobile-notification-icon" aria-hidden="true" />
              {notificationCount > 0 && (
                <span className="main-header-component-mobile-notification-count">
                  {notificationCount}
                </span>
              )}
            </button>
            <ProfileSwitchButton className="sidebar-profile-toolbar-avatar" />
          </div>
  
          {isCartOpen && <Cart />}
          {isWishlistOpen && <Wishlist />}
        </div>

        {open && (
          <div className="main-header-component-sidebar" onClick={() => setOpen(false)}>
            <div className="main-header-component-sidebar-inner" onClick={(event) => event.stopPropagation()}>
              <div className="main-header-component-sidebar-top">
              <img 
                src="https://res.cloudinary.com/dzlopmfj8/image/upload/v1724854900/memes/evajdtncfvfdnrihmx6t.png" 
                alt="Menu Icon" 
                className="mobile-header-icon" 
              />
                <h2 className="main-header-component-sidebar-title">Explore</h2>
                <div
                  className="main-header-component-wishlist-icon"
                  onClick={() => {
                    dispatch(toggleWishlist());
                    setOpen(false); 
                  }}
                >
                  <AiOutlineStar size={30} />
                  {wishlistCount > 0 && (
                    <span className="main-header-component-wishlist-count">
                      {wishlistCount}
                    </span>
                  )}
                </div>
                <RxCross1
                  size={30}
                  className="main-header-component-close-icon"
                  onClick={() => setOpen(false)}
                />
              </div>

              <div className="main-header-component-search-box">
                <SearchBar />
              </div>

              <TopLevelDropdown
                categoriesData={categoriesData}
                setDropDown={setDropDown}
              />
            </div>
          </div>
        )}
      </div>

      {isMobile && isSearchModalOpen && (
        <div className="mobile-search-modal" onClick={() => setIsSearchModalOpen(false)}>
          <div className="mobile-search-modal-content" onClick={(event) => event.stopPropagation()}>
            <AiOutlineClose 
              size={24} 
              className="close-mobile-search-modal" 
              onClick={() => setIsSearchModalOpen(false)} 
            />
            <MobileSearchBar onClose={() => setIsSearchModalOpen(false)} />
          </div>
        </div>
      )}
      <NotificationSidebar />
    </>
  );
};

export default Header;

